// Settings modal: signature editor + labels CRUD.

import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";
import { I } from "../../shared/Icon";
import type { MailFolder, MailLabel, MailRule, Signature } from "./types";
import { FOLDER_LABELS, flattenFolders } from "./types";

type Props = { open: boolean; onClose: () => void; onChange: () => void; asPage?: boolean };

// System folders selectable as a rule's move target (custom folders added at runtime).
const RULE_FOLDERS = ["INBOX", "SPAM", "TRASH"] as const;
type Tab = "signature" | "labels" | "filters" | "folders";
type RuleDraft = Omit<MailRule, "id" | "sort_order">;
const EMPTY_RULE: RuleDraft = {
  name: "", enabled: true, match_all: true,
  from_contains: null, to_contains: null, subject_contains: null, body_contains: null,
  move_to_folder: null, add_label_id: null, mark_read: false, mark_important: false, mark_starred: false,
};

const COLORS = [
  "#6E6FCF", "#2563EB", "#0891B2", "#16A34A", "#D97706", "#DC2626",
  "#DB2777", "#7C3AED", "#475569", "#0F766E", "#7C2D12", "#111827",
];

export function MailSettings({ open, onClose, onChange, asPage }: Props) {
  const [tab, setTab] = useState<Tab>("signature");
  const [sig, setSig] = useState<Signature>({ body_html: null, body_text: "", auto_attach: true });
  const [labels, setLabels] = useState<MailLabel[]>([]);
  const [newLabel, setNewLabel] = useState<{ name: string; color: string }>({ name: "", color: COLORS[0] });
  const [rules, setRules] = useState<MailRule[]>([]);
  const [folders, setFolders] = useState<MailFolder[]>([]);

  const loadRules = () => api.get<MailRule[]>("/mail/rules/").then((r) => setRules(r.data)).catch(() => {});
  const loadFolders = () => api.get<MailFolder[]>("/mail/folders/").then((r) => setFolders(r.data)).catch(() => {});

  useEffect(() => {
    if (!open) return;
    void (async () => {
      const [sigRes, lblRes] = await Promise.all([
        api.get<Signature>("/mail/signature/").catch(() => null),
        api.get<MailLabel[]>("/mail/labels/").catch(() => ({ data: [] })),
      ]);
      if (sigRes) setSig(sigRes.data);
      setLabels((lblRes as any).data || []);
      void loadRules();
      void loadFolders();
    })();
  }, [open]);

  if (!open) return null;

  // ── 내 메일함(custom folders) CRUD ──
  const createFolder = async (name: string) => {
    if (!name.trim()) return;
    try { await api.post("/mail/folders/", { name: name.trim() }); toast.success("메일함 추가됨"); loadFolders(); onChange(); }
    catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message || e?.response?.data?.detail); }
  };
  const renameFolder = async (f: MailFolder, name: string) => {
    if (!name.trim() || name === f.name) return;
    try { await api.patch(`/mail/folders/${f.id}`, { name: name.trim() }); loadFolders(); onChange(); }
    catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.error?.message || e?.response?.data?.detail); }
  };
  const reorderFolder = async (f: MailFolder, dir: "up" | "down") => {
    try { await api.patch(`/mail/folders/${f.id}`, { reorder: dir }); loadFolders(); onChange(); }
    catch (e: any) { toast.error("이동 실패", e?.response?.data?.error?.message); }
  };
  const removeFolder = async (f: MailFolder) => {
    if (!confirm(`"${f.name}" 메일함을 삭제할까요? 안의 메일은 받은 메일함으로 이동됩니다.`)) return;
    try { await api.delete(`/mail/folders/${f.id}`); toast.success("삭제됨"); loadFolders(); onChange(); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message); }
  };

  const saveSig = async () => {
    try {
      await api.put("/mail/signature/", sig);
      toast.success("서명 저장됨");
      onChange();
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.error?.message);
    }
  };

  const createLabel = async () => {
    if (!newLabel.name.trim()) return;
    try {
      const r = await api.post<MailLabel>("/mail/labels/", newLabel);
      setLabels((cur) => [...cur, r.data]);
      setNewLabel({ name: "", color: COLORS[0] });
      onChange();
    } catch (e: any) {
      toast.error("라벨 생성 실패", e?.response?.data?.error?.message);
    }
  };

  const updateLabel = async (l: MailLabel, patch: Partial<MailLabel>) => {
    try {
      const r = await api.patch<MailLabel>(`/mail/labels/${l.id}`, patch);
      setLabels((cur) => cur.map((x) => (x.id === l.id ? r.data : x)));
      onChange();
    } catch (e: any) {
      toast.error("라벨 수정 실패", e?.response?.data?.error?.message);
    }
  };

  const removeLabel = async (l: MailLabel) => {
    if (!confirm(`"${l.name}" 라벨을 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/mail/labels/${l.id}`);
      setLabels((cur) => cur.filter((x) => x.id !== l.id));
      onChange();
    } catch (e: any) {
      toast.error("라벨 삭제 실패", e?.response?.data?.error?.message);
    }
  };

  const saveRule = async (draft: RuleDraft, id?: number) => {
    if (!draft.name.trim()) { toast.warn("필터 이름을 입력하세요"); return false; }
    try {
      if (id) await api.patch(`/mail/rules/${id}`, draft);
      else await api.post("/mail/rules/", draft);
      toast.success(id ? "필터 수정됨" : "필터 추가됨");
      void loadRules();
      return true;
    } catch (e: any) {
      toast.error("필터 저장 실패", e?.response?.data?.error?.message);
      return false;
    }
  };

  const toggleRule = async (r: MailRule) => {
    try { await api.patch(`/mail/rules/${r.id}`, { enabled: !r.enabled }); void loadRules(); }
    catch (e: any) { toast.error("변경 실패", e?.response?.data?.error?.message); }
  };

  const removeRule = async (r: MailRule) => {
    if (!confirm(`"${r.name}" 필터를 삭제하시겠습니까?`)) return;
    try { await api.delete(`/mail/rules/${r.id}`); setRules((cur) => cur.filter((x) => x.id !== r.id)); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message); }
  };

  return (
    <div className={asPage ? "mail-settings-page" : "t-overlay"} onClick={asPage ? undefined : onClose}>
      <div className={asPage ? "mail-settings-card" : "t-cmdk"} onClick={(e) => e.stopPropagation()}
           style={asPage ? undefined : { width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          {asPage && <button className="t-iconbtn" onClick={onClose} title="뒤로"><I.ArrowR size={18} style={{ transform: "scaleX(-1)" }}/></button>}
          <h3 style={{ margin: 0, flex: 1, fontSize: 15, fontWeight: 700 }}>메일함 환경설정</h3>
          {!asPage && <button className="t-iconbtn" onClick={onClose}><I.X size={16}/></button>}
        </div>
        <div className="mail-settings-tabs">
          <button className={tab === "signature" ? "active" : ""} onClick={() => setTab("signature")}>서명</button>
          <button className={tab === "labels" ? "active" : ""} onClick={() => setTab("labels")}>라벨</button>
          <button className={tab === "filters" ? "active" : ""} onClick={() => setTab("filters")}>필터</button>
          <button className={tab === "folders" ? "active" : ""} onClick={() => setTab("folders")}>내 메일함</button>
        </div>
        <div style={{ padding: 18, overflow: "auto", flex: 1 }}>
          {tab === "signature" && (
            <>
              <label className="t-label">서명 본문</label>
              <textarea
                className="t-textarea"
                style={{ minHeight: 140, width: "100%" }}
                placeholder="발송 메일 하단에 자동으로 추가됩니다"
                value={sig.body_text || ""}
                onChange={(e) => setSig({ ...sig, body_text: e.target.value })}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13, color: "var(--text-2)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={sig.auto_attach}
                  onChange={(e) => setSig({ ...sig, auto_attach: e.target.checked })}
                />
                새 메일 작성 시 자동으로 서명 추가
              </label>
              <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                <button className="t-btn t-btn-primary" onClick={saveSig}>저장</button>
              </div>
            </>
          )}
          {tab === "labels" && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label className="t-label">새 라벨</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    className="t-input"
                    placeholder="라벨 이름"
                    value={newLabel.name}
                    onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") createLabel(); }}
                    style={{ flex: 1 }}
                  />
                  <button className="t-btn t-btn-primary" onClick={createLabel}>
                    <I.Plus size={12}/> 추가
                  </button>
                </div>
                <div className="color-grid" style={{ marginTop: 8 }}>
                  {COLORS.map((c) => (
                    <div key={c} className={`ci ${newLabel.color === c ? "active" : ""}`}
                         style={{ background: c }}
                         onClick={() => setNewLabel({ ...newLabel, color: c })}/>
                  ))}
                </div>
              </div>

              <label className="t-label">내 라벨</label>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {labels.length === 0 && (
                  <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
                    아직 라벨이 없습니다
                  </div>
                )}
                {labels.map((l) => (
                  <LabelRow key={l.id} label={l} onUpdate={updateLabel} onRemove={removeLabel}/>
                ))}
              </div>
            </>
          )}
          {tab === "filters" && (
            <FiltersTab rules={rules} labels={labels} folders={folders} onSave={saveRule} onToggle={toggleRule} onRemove={removeRule}/>
          )}
          {tab === "folders" && (
            <FoldersTab folders={folders} onCreate={createFolder} onRename={renameFolder} onReorder={reorderFolder} onRemove={removeFolder}/>
          )}
        </div>
      </div>
    </div>
  );
}

function LabelRow({ label, onUpdate, onRemove }:
  { label: MailLabel; onUpdate: (l: MailLabel, p: Partial<MailLabel>) => void; onRemove: (l: MailLabel) => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(label.name);
  const [pickerOpen, setPickerOpen] = useState(false);

  const save = () => {
    setEditing(false);
    if (name !== label.name) onUpdate(label, { name });
  };

  return (
    <div className="label-row">
      <div style={{ position: "relative" }}>
        <span className="swatch" style={{ background: label.color }} onClick={() => setPickerOpen(!pickerOpen)}/>
        {pickerOpen && (
          <div className="color-grid" style={{ position: "absolute", top: 22, left: 0, zIndex: 30, width: 196 }}>
            {COLORS.map((c) => (
              <div key={c} className={`ci ${label.color === c ? "active" : ""}`}
                   style={{ background: c }}
                   onClick={() => { onUpdate(label, { color: c }); setPickerOpen(false); }}/>
            ))}
          </div>
        )}
      </div>
      {editing ? (
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={save} autoFocus
               onKeyDown={(e) => { if (e.key === "Enter") save(); }}/>
      ) : (
        <span className="name" onClick={() => setEditing(true)} style={{ cursor: "text" }}>{label.name}</span>
      )}
      <button onClick={() => onRemove(label)} aria-label="삭제">
        <I.Trash size={14}/>
      </button>
    </div>
  );
}

// ── Filters / rules tab ───────────────────────────────────────────────────
function FiltersTab({ rules, labels, folders, onSave, onToggle, onRemove }: {
  rules: MailRule[];
  labels: MailLabel[];
  folders: MailFolder[];
  onSave: (draft: RuleDraft, id?: number) => Promise<boolean>;
  onToggle: (r: MailRule) => void;
  onRemove: (r: MailRule) => void;
}) {
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const folderName = (code: string): string =>
    FOLDER_LABELS[code] || folders.find((f) => f.code === code)?.name || code;

  const summary = (r: MailRule): string => {
    const conds: string[] = [];
    if (r.from_contains) conds.push(`보낸사람⊃"${r.from_contains}"`);
    if (r.to_contains) conds.push(`받는사람⊃"${r.to_contains}"`);
    if (r.subject_contains) conds.push(`제목⊃"${r.subject_contains}"`);
    if (r.body_contains) conds.push(`본문⊃"${r.body_contains}"`);
    const acts: string[] = [];
    if (r.move_to_folder) acts.push(`→${folderName(r.move_to_folder)}`);
    const lbl = labels.find((l) => l.id === r.add_label_id);
    if (lbl) acts.push(`라벨:${lbl.name}`);
    if (r.mark_read) acts.push("읽음");
    if (r.mark_important) acts.push("중요");
    if (r.mark_starred) acts.push("별표");
    return `${conds.join(r.match_all ? " 그리고 " : " 또는 ") || "(조건 없음)"}  ⇒  ${acts.join(", ") || "(동작 없음)"}`;
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--text-3)", flex: 1 }}>받는 메일이 조건에 맞으면 자동으로 분류·표시합니다 (위에서부터 순서대로 적용).</div>
        <button className="t-btn t-btn-primary" onClick={() => setEditingId("new")}><I.Plus size={12}/> 새 필터</button>
      </div>

      {editingId === "new" && (
        <div style={{ marginBottom: 10 }}>
          <RuleForm labels={labels} folders={folders} onCancel={() => setEditingId(null)}
            onSubmit={async (d) => { if (await onSave(d)) setEditingId(null); }}/>
        </div>
      )}

      {rules.length === 0 && editingId !== "new" && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>등록된 필터가 없습니다</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rules.map((r) => (
          editingId === r.id ? (
            <RuleForm key={r.id} labels={labels} folders={folders} initial={r} onCancel={() => setEditingId(null)}
              onSubmit={async (d) => { if (await onSave(d, r.id)) setEditingId(null); }}/>
          ) : (
            <div key={r.id} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
              <input type="checkbox" checked={r.enabled} onChange={() => onToggle(r)} title="사용/중지"/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, opacity: r.enabled ? 1 : 0.5 }}>{r.name}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary(r)}</div>
              </div>
              <button className="t-btn t-btn-sm" onClick={() => setEditingId(r.id)}>편집</button>
              <button className="t-iconbtn" onClick={() => onRemove(r)} aria-label="삭제"><I.Trash size={14}/></button>
            </div>
          )
        ))}
      </div>
    </>
  );
}

function RuleForm({ labels, folders, initial, onSubmit, onCancel }: {
  labels: MailLabel[];
  folders: MailFolder[];
  initial?: MailRule;
  onSubmit: (d: RuleDraft) => void;
  onCancel: () => void;
}) {
  const [d, setD] = useState<RuleDraft>(initial ? {
    name: initial.name, enabled: initial.enabled, match_all: initial.match_all,
    from_contains: initial.from_contains, to_contains: initial.to_contains,
    subject_contains: initial.subject_contains, body_contains: initial.body_contains,
    move_to_folder: initial.move_to_folder, add_label_id: initial.add_label_id,
    mark_read: initial.mark_read, mark_important: initial.mark_important, mark_starred: initial.mark_starred,
  } : { ...EMPTY_RULE });
  const set = (patch: Partial<RuleDraft>) => setD((c) => ({ ...c, ...patch }));

  const cond = (key: "from_contains" | "to_contains" | "subject_contains" | "body_contains", label: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 64, fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{label}</span>
      <input className="t-input" style={{ flex: 1 }} placeholder="포함 텍스트 (비우면 무시)"
        value={d[key] ?? ""} onChange={(e) => set({ [key]: e.target.value || null } as Partial<RuleDraft>)}/>
    </div>
  );
  const labelCol: React.CSSProperties = { width: 64, fontSize: 12, color: "var(--text-3)", flexShrink: 0 };

  return (
    <div style={{ border: "1px solid var(--p-400, #818cf8)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8, background: "var(--panel)" }}>
      <input className="t-input" placeholder="필터 이름" value={d.name} onChange={(e) => set({ name: e.target.value })}/>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", marginTop: 2 }}>조건</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={labelCol}>결합</span>
        <select className="t-input" style={{ width: 150 }} value={d.match_all ? "all" : "any"} onChange={(e) => set({ match_all: e.target.value === "all" })}>
          <option value="all">모두 만족 (AND)</option>
          <option value="any">하나라도 (OR)</option>
        </select>
      </div>
      {cond("from_contains", "보낸사람")}
      {cond("to_contains", "받는사람")}
      {cond("subject_contains", "제목")}
      {cond("body_contains", "본문")}
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", marginTop: 2 }}>동작</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={labelCol}>이동</span>
        <select className="t-input" style={{ flex: 1 }} value={d.move_to_folder ?? ""} onChange={(e) => set({ move_to_folder: e.target.value || null })}>
          <option value="">(이동 안 함)</option>
          {RULE_FOLDERS.map((f) => <option key={f} value={f}>{FOLDER_LABELS[f]}</option>)}
          {folders.length > 0 && <optgroup label="내 메일함">
            {/* 규칙에서 고를 때도 사이드바와 같은 계층·순서로 보여 준다. */}
            {flattenFolders(folders).map(({ folder: f, depth }) => (
              <option key={f.code} value={f.code}>
                {`${"  ".repeat(depth)}${depth > 0 ? "└ " : ""}${f.name}`}
              </option>
            ))}
          </optgroup>}
        </select>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={labelCol}>라벨</span>
        <select className="t-input" style={{ flex: 1 }} value={d.add_label_id ?? ""} onChange={(e) => set({ add_label_id: e.target.value ? Number(e.target.value) : null })}>
          <option value="">(라벨 없음)</option>
          {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, marginTop: 2 }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}><input type="checkbox" checked={d.mark_read} onChange={(e) => set({ mark_read: e.target.checked })}/>읽음 처리</label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}><input type="checkbox" checked={d.mark_important} onChange={(e) => set({ mark_important: e.target.checked })}/>중요 표시</label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}><input type="checkbox" checked={d.mark_starred} onChange={(e) => set({ mark_starred: e.target.checked })}/>별표</label>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
        <button className="t-btn t-btn-sm" onClick={onCancel}>취소</button>
        <button className="t-btn t-btn-sm t-btn-primary" onClick={() => onSubmit(d)}>저장</button>
      </div>
    </div>
  );
}

// ── 내 메일함 (custom folders) tab ─────────────────────────────────────────
function FoldersTab({ folders, onCreate, onRename, onReorder, onRemove }: {
  folders: MailFolder[];
  onCreate: (name: string) => void;
  onRename: (f: MailFolder, name: string) => void;
  onReorder: (f: MailFolder, dir: "up" | "down") => void;
  onRemove: (f: MailFolder) => void;
}) {
  const [name, setName] = useState("");
  const add = () => { if (name.trim()) { onCreate(name.trim()); setName(""); } };

  return (
    <>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 12 }}>
        나만의 메일함을 만들어 메일을 분류할 수 있습니다. <b>필터</b> 탭에서 조건에 맞는 메일을 이 메일함으로 자동 이동시키거나,
        메일 목록에서 우클릭 → 이동으로 옮길 수 있습니다.
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input className="t-input" style={{ flex: 1 }} placeholder="새 메일함 이름 (예: 거래처, 영수증)"
          value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}/>
        <button className="t-btn t-btn-primary" onClick={add}><I.Plus size={12}/> 추가</button>
      </div>
      {folders.length === 0 && (
        <div style={{ padding: 20, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>아직 메일함이 없습니다</div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {folders.map((f, i) => (
          <FolderRow key={f.id} folder={f}
            isFirst={i === 0} isLast={i === folders.length - 1}
            onRename={onRename} onReorder={onReorder} onRemove={onRemove}/>
        ))}
      </div>
    </>
  );
}

function FolderRow({ folder, isFirst, isLast, onRename, onReorder, onRemove }: {
  folder: MailFolder; isFirst: boolean; isLast: boolean;
  onRename: (f: MailFolder, name: string) => void;
  onReorder: (f: MailFolder, dir: "up" | "down") => void;
  onRemove: (f: MailFolder) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const save = () => { setEditing(false); onRename(folder, name); };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px" }}>
      <span>📂</span>
      {editing ? (
        <input className="t-input" style={{ flex: 1 }} value={name} autoFocus
          onChange={(e) => setName(e.target.value)} onBlur={save}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setName(folder.name); setEditing(false); } }}/>
      ) : (
        <span style={{ flex: 1, fontSize: 13, cursor: "text" }} onClick={() => setEditing(true)}>{folder.name}</span>
      )}
      <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>{folder.total}건{folder.unread > 0 ? ` · 안읽음 ${folder.unread}` : ""}</span>
      <button className="t-iconbtn" disabled={isFirst} title="위로" onClick={() => onReorder(folder, "up")}>↑</button>
      <button className="t-iconbtn" disabled={isLast} title="아래로" onClick={() => onReorder(folder, "down")}>↓</button>
      <button className="t-iconbtn" title="이름 변경" onClick={() => setEditing(true)}><I.Edit size={14}/></button>
      <button className="t-iconbtn" title="삭제" onClick={() => onRemove(folder)}><I.Trash size={14}/></button>
    </div>
  );
}
